import { API, deleteRequest, getRequest, patchRequest, postRequest } from '@/api/api';
import { isNotFound } from '@/common/error';

import {
  mapCancelSubscriptionToDto,
  mapCreateCheckoutToDto,
  mapPrice,
  mapSubscription,
  mapSubscriptionsPage,
  mapUpdateSubscriptionToDto,
} from './subscriptions.mappers';
import type {
  CancelSubscription,
  CreateCheckoutSubscription,
  GetAllSubscriptionsParams,
  GetSubscriptionParams,
  Price,
  PriceDto,
  StripeUrlDto,
  Subscription,
  SubscriptionDto,
  SubscriptionsPage,
  SubscriptionsPageDto,
  SubscriptionMutationParams,
  UpdateSubscription,
} from './subscriptions.types';

/**
 * Every endpoint here is scoped by the `X-Service-Id` header, so `serviceId` is an
 * explicit argument rather than ambient state. For caller-scoped reads
 * (`/subscriptions/all`) any service the caller belongs to satisfies the middleware;
 * for `GET /subscriptions` and the mutations it selects which subscription is meant.
 */

/**
 * GET /subscriptions → that service's single subscription, in whatever state it is in.
 * A service with no subscription answers 404 rather than an empty body, which is
 * mapped to `null` — a normal state, not a failure.
 */
export const getSubscription = async ({
  serviceId,
}: GetSubscriptionParams): Promise<Subscription | null> => {
  try {
    const { data } = await getRequest<SubscriptionDto>(
      API.PEOPLE,
      '/subscriptions',
      false,
      undefined,
      serviceId,
    );
    return mapSubscription(data);
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
};

/**
 * How `/subscriptions/all` is walked when the whole set is wanted. The page size is large
 * because the set is small — one row per service the caller pays for — and the cap only
 * exists so a server that keeps answering with a full page cannot spin forever.
 */
const BILLED_PAGE_SIZE = 100;
const BILLED_MAX_PAGES = 20;

/** GET /subscriptions/all → every subscription the caller owns, newest first. */
export const getAllSubscriptions = async ({
  serviceId,
  limit = 50,
  page = 1,
}: GetAllSubscriptionsParams): Promise<SubscriptionsPage> => {
  const { data } = await getRequest<SubscriptionsPageDto>(
    API.PEOPLE,
    '/subscriptions/all',
    false,
    { limit, page },
    serviceId,
  );
  return mapSubscriptionsPage(data);
};

/**
 * The ids of every service the caller is *billed for*, folded out of `/subscriptions/all`.
 *
 * This is the only way to tell whose card a vessel's plan is on: `GET /subscriptions`
 * describes the plan but not its payer — `SubscriptionResponse.PersonID` is `json:"-"` in the
 * People service's `pkg/dto/subscription.go`, so no field on it names one. `/subscriptions/all`
 * filters on exactly that column, so a vessel missing from this set is on a plan somebody
 * else pays for, which is what separates changing a plan from taking it over.
 *
 * Paged through to the end rather than read one page deep: a truncated set would report a
 * vessel the caller pays for as somebody else's, and offer to buy a plan the server would
 * refuse with `ErrSubscriptionExists`.
 */
export const getBilledServiceIds = async ({
  serviceId,
}: GetSubscriptionParams): Promise<Set<string>> => {
  const serviceIds = new Set<string>();
  let read = 0;

  for (let page = 1; page <= BILLED_MAX_PAGES; page++) {
    const { count, items } = await getAllSubscriptions({
      serviceId,
      limit: BILLED_PAGE_SIZE,
      page,
    });

    items.forEach((subscription) => serviceIds.add(subscription.serviceId));
    read += items.length;

    if (items.length === 0 || read >= count) break;
  }

  return serviceIds;
};

/**
 * GET /subscriptions/prices.
 *
 * The parameter is `lookup_keys`, repeated once per key — not the `lookupKeys` CSV the
 * spec documents. The People service binds it from
 * `GetPricesRequest.LookupKeys []string \`query:"lookup_keys" validate:"required"\``
 * (`pkg/dto/subscription.go`), so the swagger annotation is simply wrong: sending
 * `lookupKeys` answers `Field validation for 'LookupKeys' failed on the 'required' tag`.
 * Echo's binder reads every value of the key for a slice field and does not split commas,
 * which is why `paramsSerializer: { indexes: null }` on the instance matters — it emits
 * `lookup_keys=a&lookup_keys=b` rather than indexing the array.
 */
export const getSubscriptionPrices = async ({
  serviceId,
  lookupKeys,
}: {
  serviceId: string;
  lookupKeys: string[];
}): Promise<Price[]> => {
  const { data } = await getRequest<PriceDto[]>(
    API.PEOPLE,
    '/subscriptions/prices',
    false,
    { lookup_keys: lookupKeys },
    serviceId,
  );
  return (data ?? []).map(mapPrice);
};

/**
 * POST /subscriptions/checkout → Stripe checkout URL to redirect the user to. The
 * subscription is created for the service named in the header.
 *
 * The row exists as `incomplete` the moment this resolves — before the user has paid.
 * It only flips to active once Stripe's webhook lands, so a user who abandons the
 * Stripe page leaves an incomplete row behind.
 *
 * Rejects with 403 when the caller already has the maximum number of paid services,
 * which needs a "contact sales" branch rather than an error toast. That case is not
 * distinguishable from a 400 here: the api client discards the HTTP status and the
 * spec documents no domain/type for it.
 */
export const createSubscriptionCheckout = async ({
  serviceId,
  input,
}: {
  serviceId: string;
  input: CreateCheckoutSubscription;
}): Promise<string> => {
  const { data } = await postRequest<StripeUrlDto, unknown>(
    API.PEOPLE,
    '/subscriptions/checkout',
    mapCreateCheckoutToDto(input),
    false,
    serviceId,
  );
  return data.url;
};

/**
 * PATCH /subscriptions/{id} — change tier, interval or seat counts.
 *
 * Answers either 204 (applied) or 200 with a Stripe URL (the subscription was on
 * `harbor`, which has no Stripe object to modify, so the server started a checkout
 * instead). Returns that URL when present, `null` when the change was applied.
 *
 * A harbor vessel is sent to `POST /subscriptions/checkout` directly rather than through
 * here — that is the same checkout the server would have started, one hop shorter, and it
 * does not need tier, interval and seats to arrive together to avoid
 * `ErrHarborUpgradeNeedsPlan`. So in practice this only ever gets a live paid row and only
 * ever answers 204; the URL is still returned in case one reaches it another way.
 *
 * Seat proration is asymmetric: increases invoice immediately, decreases carry a
 * credit instead of refunding.
 *
 * A 502 here means Stripe could not price the change and nothing was altered, so it is
 * safe to retry — but, as with the 403 above, it cannot currently be told apart from a
 * 500 because the thrown error carries no status.
 */
export const updateSubscription = async ({
  serviceId,
  id,
  input,
}: SubscriptionMutationParams & { input: UpdateSubscription }): Promise<string | null> => {
  const response = await patchRequest<StripeUrlDto, unknown>(
    API.PEOPLE,
    `/subscriptions/${id}`,
    mapUpdateSubscriptionToDto(input),
    false,
    serviceId,
  );
  return response.status === 200 ? (response.data?.url ?? null) : null;
};

/**
 * POST /subscriptions/{id}/cancel — schedules cancellation at the end of the current
 * period. The subscription stays active until `cancelAt`.
 */
export const cancelSubscription = async ({
  serviceId,
  id,
  input = {},
}: SubscriptionMutationParams & { input?: CancelSubscription }) => {
  await postRequest(
    API.PEOPLE,
    `/subscriptions/${id}/cancel`,
    mapCancelSubscriptionToDto(input),
    false,
    serviceId,
  );
};

/** DELETE /subscriptions/{id}/uncancel — clears a pending cancellation. */
export const uncancelSubscription = async ({ serviceId, id }: SubscriptionMutationParams) => {
  await deleteRequest(
    API.PEOPLE,
    `/subscriptions/${id}/uncancel`,
    undefined,
    false,
    undefined,
    serviceId,
  );
};

/** POST /subscriptions/portal → Stripe billing portal URL (payment methods, invoices). */
export const createSubscriptionPortal = async ({
  serviceId,
}: GetSubscriptionParams): Promise<string> => {
  const { data } = await postRequest<StripeUrlDto, undefined>(
    API.PEOPLE,
    '/subscriptions/portal',
    undefined,
    false,
    serviceId,
  );
  return data.url;
};
